import axios from "axios";

// Use environment variable for API URL, with a fallback
// Make sure we're using the correct port for the API
const CHAT_API_URL =
  import.meta.env.VITE_CHAT_API_URL ||
  (window.location.hostname === "localhost"
    ? `${window.location.protocol}//${window.location.hostname}:8000/api/chat`
    : "/api/chat");

// Communication API URL for full message history
const COMM_API_URL =
  import.meta.env.VITE_COMM_API_URL ||
  (window.location.hostname === "localhost"
    ? `${window.location.protocol}//${window.location.hostname}:8000/api/communication`
    : "/api/communication");

// Helper function to get headers with token
const getAuthHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const token =
    localStorage.getItem("adminToken") ||
    localStorage.getItem("staffToken") ||
    localStorage.getItem("customerToken") ||
    localStorage.getItem("bankingPartnerToken") ||
    localStorage.getItem("affiliatePartnerToken") ||
    localStorage.getItem("creditManagerToken") ||
    localStorage.getItem("token");

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

// Get user ID from token
const getUserId = () => {
  try {
    const token = getAuthHeaders().Authorization?.replace("Bearer ", "");
    if (!token) return null;

    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64));
    return payload.userId || payload.id;
  } catch (error) {
    console.error("Error getting user ID:", error);
    return null;
  }
};

// Get conversations
export const getConversations = async () => {
  try {
    // Try communication API first
    try {
      const res = await axios.get(`${COMM_API_URL}/conversations`, {
        headers: getAuthHeaders(),
      });

      // Communication API returns conversations in an object with a conversations array
      if (
        res.data &&
        res.data.conversations &&
        Array.isArray(res.data.conversations)
      ) {
        console.log(
          `Found ${res.data.conversations.length} conversations from communication API`
        );

        // Map communication conversations to the format expected by the UI
        const mappedConversations = res.data.conversations.map((conv) => ({
          _id: conv._id || conv.conversationId,
          conversationId: conv.conversationId || conv._id,
          // Find the participant that is not the current user
          participant:
            conv.participants?.find((p) => p.userId._id !== getUserId()) || {},
          lastMessageSnippet: conv.lastMessage?.content || "",
          lastMessageAt: conv.lastMessage?.timestamp || conv.updatedAt,
        }));

        // Filter out default users
        const defaultNames = [
          "affiliate123",
          "staff",
          "default",
          "affiliate",
          "customer",
          "user",
        ];
        return mappedConversations.filter((conv) => {
          const name = (conv.participant?.name || "").toLowerCase();
          return !defaultNames.includes(name) && name.length > 2;
        });
      }

      throw new Error("No conversations found in communication API");
    } catch (commErr) {
      console.log(
        "Communication API failed, trying chat API:",
        commErr.message
      );

      // Try chat API as fallback
      const res = await axios.get(`${CHAT_API_URL}/conversations`, {
        headers: getAuthHeaders(),
      });

      // Filter out default users
      const defaultNames = [
        "affiliate123",
        "staff",
        "default",
        "affiliate",
        "customer",
        "user",
      ];
      let conversations = Array.isArray(res.data)
        ? res.data
        : res.data?.conversations || res.data?.data || [];

      return conversations.filter((conv) => {
        const name = (
          conv.participant?.name ||
          conv.receiver?.name ||
          conv.user?.name ||
          ""
        ).toLowerCase();
        return !defaultNames.includes(name) && name.length > 2;
      });
    }
  } catch (error) {
    console.error("Error fetching conversations:", error);
    return [];
  }
};

// Get messages for a conversation
export const getMessages = async (conversationId) => {
  try {
    // Extract conversation ID if object is passed
    let convId = conversationId;
    if (typeof conversationId === "object") {
      convId = conversationId.conversationId || conversationId._id;
    }

    if (!convId) {
      console.error("No conversation ID provided");
      return [];
    }

    // Try communication API first (has full message history)
    try {
      const res = await axios.get(
        `${COMM_API_URL}/conversations/${convId}/messages`,
        {
          headers: getAuthHeaders(),
        }
      );

      // Communication API returns messages in an object with a messages array
      if (res.data && res.data.messages && Array.isArray(res.data.messages)) {
        console.log(
          `Found ${res.data.messages.length} messages from communication API`
        );
        return res.data.messages;
      }

      // If no messages found, try chat API
      throw new Error("No messages found in communication API");
    } catch (err) {
      console.log("Communication API failed, trying chat API:", err.message);

      // Try chat API
      try {
        const res = await axios.get(`${CHAT_API_URL}/messages/${convId}`, {
          headers: getAuthHeaders(),
        });
        return Array.isArray(res.data)
          ? res.data
          : res.data?.messages || res.data?.chatHistory || [];
      } catch (chatErr) {
        // Try legacy endpoint
        const userId = getUserId();
        if (!userId) return [];

        // Extract receiver ID
        let receiverId = conversationId;
        if (typeof conversationId === "object") {
          receiverId =
            conversationId.receiverId ||
            (conversationId.participant && conversationId.participant._id);
        }

        if (!receiverId) return [];

        // Get chat history
        const historyRes = await axios.get(`${CHAT_API_URL}/history`, {
          headers: getAuthHeaders(),
          params: {
            user1Id: userId,
            user1Type: "Staff",
            user2Id: receiverId,
            user2Type: "Credit Manager",
          },
        });

        return historyRes.data?.chatHistory || [];
      }
    }
  } catch (error) {
    console.error("Error fetching messages:", error);
    return [];
  }
};

// Get participant details
export const getParticipantDetails = async (participantId) => {
  try {
    const res = await axios.get(
      `${CHAT_API_URL}/participant/${participantId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching participant details:", error);
    return null;
  }
};

// Send message
export const sendMessage = async (conversationId, text) => {
  try {
    // Get conversation details
    let receiverId, receiverType, convId;

    if (typeof conversationId === "object") {
      receiverId =
        conversationId.receiverId ||
        (conversationId.participant && conversationId.participant._id);
      receiverType =
        conversationId.receiverType ||
        (conversationId.participant && conversationId.participant.userType) ||
        "Credit Manager";
      convId = conversationId.conversationId || conversationId._id;
    } else {
      convId = conversationId;
    }

    // Try communication API first (stores full message history)
    try {
      const res = await axios.post(
        `${COMM_API_URL}/messages`,
        {
          conversationId: convId,
          content: text,
          messageType: "text",
        },
        { headers: getAuthHeaders() }
      );

      if (res.data && (res.data.message || res.data.success)) {
        return res.data.message || res.data;
      }

      throw new Error("Communication API failed");
    } catch (commErr) {
      console.log(
        "Communication API failed, trying chat API:",
        commErr.message
      );

      // Try chat API
      try {
        const res = await axios.post(
          `${CHAT_API_URL}/message`,
          { conversationId: convId, text },
          { headers: getAuthHeaders() }
        );
        return res.data;
      } catch (err) {
        // Try legacy endpoint
        const userId = getUserId();
        if (!userId || !receiverId) {
          throw new Error("Missing user ID or receiver ID");
        }

        // Format receiver type
        const formattedType = receiverType
          ? receiverType.charAt(0).toUpperCase() +
            receiverType.slice(1).replace(/-/g, " ")
          : "Credit Manager";

        const res = await axios.post(
          `${CHAT_API_URL}/send`,
          {
            senderId: userId,
            senderType: "Staff",
            receiverId: receiverId,
            receiverType: formattedType,
            message: text,
          },
          { headers: getAuthHeaders() }
        );

        return res.data.data || res.data;
      }
    }
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

// Get employees by role
export const getEmployeesByRole = async (role) => {
  try {
    const res = await axios.get(`${CHAT_API_URL}/employees`, {
      params: { role },
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching employees:", error);
    return [];
  }
};

// Start conversation
export const startConversation = async (employeeId, receiverType = "staff") => {
  try {
    // Generate unique ID
    const conversationId = `${Date.now()}-${employeeId}`;
    const userId = getUserId();

    // Try communication API first
    try {
      // Format participants for communication API
      const participants = [
        {
          userId: userId,
          userType: "staff", // Assuming current user is staff
          isActive: true,
        },
        {
          userId: employeeId,
          userType: receiverType,
          isActive: true,
        },
      ];

      const res = await axios.post(
        `${COMM_API_URL}/conversations`,
        {
          participants,
          conversationType: "direct",
          title: `Chat with ${receiverType}`,
        },
        { headers: getAuthHeaders() }
      );

      if (res.data && res.data.conversation) {
        console.log(
          "Created conversation via communication API:",
          res.data.conversation
        );

        // Format conversation for UI
        const conversation = res.data.conversation;
        conversation.receiverType = receiverType;
        conversation.receiverId = employeeId;

        // Find the participant that is not the current user
        const participant = conversation.participants?.find(
          (p) => p.userId._id !== userId || p.userId !== userId
        );

        if (participant) {
          conversation.participant = participant.userId;
        }

        return conversation;
      }

      throw new Error("Failed to create conversation via communication API");
    } catch (commErr) {
      console.log(
        "Communication API failed, trying chat API:",
        commErr.message
      );

      // Try chat API
      try {
        const res = await axios.post(
          `${CHAT_API_URL}/conversation`,
          { employeeId, receiverType, conversationId },
          { headers: getAuthHeaders() }
        );

        const conversation = res.data.conversation || res.data;
        conversation.receiverType = receiverType;
        conversation.receiverId = employeeId;

        return conversation;
      } catch (err) {
        // Try legacy endpoint
        const formattedType = receiverType
          ? receiverType.charAt(0).toUpperCase() +
            receiverType.slice(1).replace(/-/g, " ")
          : "Credit Manager";

        await axios.post(
          `${CHAT_API_URL}/send`,
          {
            senderId: userId,
            senderType: "Staff",
            receiverId: employeeId,
            receiverType: formattedType,
            message: "Hello! Starting a new conversation.",
          },
          { headers: getAuthHeaders() }
        );

        // Return conversation object
        return {
          _id: conversationId,
          conversationId: conversationId,
          participants: [userId, employeeId],
          receiverId: employeeId,
          receiverType: receiverType,
          lastMessageSnippet: "New conversation started",
        };
      }
    }
  } catch (error) {
    console.error("Error starting conversation:", error);
    throw error;
  }
};

// Get chat history
export const getChatHistory = async (params) => {
  try {
    const res = await axios.get(`${CHAT_API_URL}/history`, {
      params,
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching chat history:", error);
    return { chatHistory: [] };
  }
};

// Completely disable socket.io connections to avoid CORS errors
export const connectSocket = () => {
  console.log("Socket.io connections disabled - using polling instead");
  return null;
};
export const disconnectSocket = () => null;
export const onNewMessage = () => null;
export const offNewMessage = () => null;
